import { App } from '@capacitor/app';
import { AuthActions, AuthService } from 'ionic-appauth';
import { first } from 'rxjs/operators';
import { CapacitorBrowser, CapacitorSecureStorage } from 'ionic-appauth/lib/capacitor';
import { isPlatform } from '@ionic/vue';
import { AxiosRequestor } from './AxiosService';
import { CapacitorRequestor } from './CapacitorService';
import { Browser } from 'ionic-appauth/lib/auth-browser';
import { ServicesBuilder } from '@/utils/setup';
import { WorkManager } from '@/workers/work.manager';
// import { shell } from 'electron';
import { Capacitor } from '@capacitor/core';
import { Browser as CapBrowser } from '@capacitor/browser';
import { useDevice } from '@/stores/device.store';
import { getPlatform } from '@/utils/misc';
import { startKeepAlive } from '@/utils/keep.alive';
import secureStorage from '@xpos/utils/src/secure.storage.utils';

export class Auth {
  private static authService: AuthService | undefined;
  private static buildAuthInstance() {
    const platform = getPlatform();
    // Web: CapacitorCompatibleBrowser catches closeWindow error that aborts OAuth token exchange
    const browser = platform === 'electron' ? new ElectronBrowser()
      : (platform === 'ios' || platform === 'android') ? new CapacitorBrowser()
      : new CapacitorCompatibleBrowser();

    const requestor = isPlatform('ios') ? new CapacitorRequestor() : new AxiosRequestor();
    const authService = new AuthService(browser, new CapacitorSecureStorage(), requestor);

    const host = window.location.origin;
    authService.authConfig = {
      client_id: import.meta.env.VITE_CLIENT_ID,
      server_host: import.meta.env.VITE_AUTH_SERVER_HOST,
      redirect_url:
        isPlatform('capacitor') || isPlatform('electron')
          ? import.meta.env.VITE_SCHEME + 'callback'
          : host + '/authcallback',
      end_session_redirect_url: isPlatform('capacitor')
        ? import.meta.env.VITE_SCHEME + 'endSession'
        : host + '/endSession',
      scopes: 'offline_access',
      pkce: true,
    };

    if (isPlatform('capacitor')) {
      App.addListener('appUrlOpen', async (data: any) => {
        console.log(data.url);
        if (data.url?.indexOf(authService.authConfig.redirect_url) === 0) {
          authService.authorizationCallback(data.url);
          // Auto-complete subscription after first SignInSuccess to prevent memory leak on repeated OAuth redirects
          Auth.Instance.events$.pipe(first((a) => a.action === AuthActions.SignInSuccess)).subscribe(() => {
            location.href = '/authcallback';
          });
        } else if (data.url.indexOf(authService.authConfig.end_session_redirect_url) === 0) {
          await authService.endSessionCallback();
          // location.href = '/authcallback';
          setTimeout(() => {
            authService.signIn();
          }, 1000);
        }
      });
    }

    authService.init();

    authService.events$.subscribe(async (action) => {
      console.log(action.action);
      if (
        action.action === AuthActions.RefreshSuccess ||
        action.action === AuthActions.SignInSuccess ||
        action.action == AuthActions.LoadTokenFromStorageSuccess
      ) {
        if (getPlatform() === 'mobile-web') return;
        if (action.tokenResponse) {
          if (Capacitor.isNativePlatform())
            localStorage.setItem('cap_sec_token_response', btoa(JSON.stringify(action.tokenResponse)));
        }
        await ServicesBuilder.initCoreServices();
        await ServicesBuilder.initWyo();
        WorkManager.getInstance().startTokenWorker(900000);
        // Skip if no device registered yet (fresh login/outlet switch) - authCallback registers the device then connects itself
        if (!secureStorage.getItem('device_uid')) return;
        startKeepAlive(); // Cold start routes a registered device straight to /pos, so authCallback.initServices never runs (#1757)
        await ServicesBuilder.initSignalR();
      }
    });

    return authService;
  }

  public static get Instance(): AuthService {
    if (!this.authService) {
      this.authService = this.buildAuthInstance();
    }

    return this.authService;
  }
}

export class ElectronBrowser extends Browser {
  private openedWindow?: Window;

  showWindow(url: string): undefined {
    if (location.hash.indexOf('#/authcallback') === 0) return;
    if (url.indexOf('connect/logout') !== -1) return;
    window.electron.openExternal(url);
    return;
  }

  closeWindow(): void {
    if (this.openedWindow) {
      this.openedWindow.close();
      this.openedWindow = undefined;
    }
  }
}

class CapacitorCompatibleBrowser extends Browser {
  _browser: CapacitorBrowser = new CapacitorBrowser();
  closeWindow(): Promise<void> {
    if (Capacitor.getPlatform() === 'android') return Promise.resolve();
    return CapBrowser.close().catch((error: any) => {
      if (error === 'No active window to close!' || error?.message === 'No active window to close!') return;
      throw error;
    });
  }
  showWindow(url: string) {
    if (isPlatform('electron')) {
      window.electron.openExternal(url);
      return Promise.resolve('');
    }
    return this._browser.showWindow(url);
  }
}
